import { useState } from 'react';
import { Link } from 'react-router';
import type { TeamRole } from '../api';
import { useSession } from '../auth/session';
import {
  useChangeRole,
  useCreateTeam,
  useDeleteTeam,
  useRemoveMember,
  useTeam,
  useTeams,
} from '../library/teamQueries';
import {
  useInviteMember,
  useResendTeamInvitation,
  useRevokeTeamInvitation,
  useTeamInvitations,
} from '../library/invitationQueries';
import { InvitationsCard } from '../library/InvitationsCard';
import { Banner, Button, EmptyState, Field, Spinner, TextInput } from '../components/ui';
import { ThemeToggle } from '../components/ThemeToggle';
import { TeamBillingPanel } from '../billing/TeamBillingPanel';
import { errorMessage } from '../api/problems';

const roles: TeamRole[] = ['Member', 'Admin', 'Owner'];

export function TeamsPage() {
  const teams = useTeams();
  const createTeam = useCreateTeam();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [name, setName] = useState('');

  const selected = selectedId ?? teams.data?.[0]?.id ?? null;

  return (
    <div className="flex h-full flex-col bg-surface">
      <header className="flex items-center justify-between border-b border-border-subtle px-6 py-3">
        <div className="flex items-center gap-3">
          <Link to="/" className="text-sm text-text-muted hover:text-text">
            ← Back to drawer
          </Link>
          <h1 className="text-sm font-semibold text-text">Teams</h1>
        </div>
        <ThemeToggle />
      </header>

      <div className="flex min-h-0 flex-1 flex-col md:flex-row">
        <aside className="w-full border-b border-border-subtle p-4 md:w-64 md:border-r md:border-b-0">
          <form
            className="flex flex-col gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              createTeam.mutate(name, {
                onSuccess: (team) => {
                  setSelectedId(team.id);
                  setName('');
                },
              });
            }}
          >
            <Field label="New team">
              {(id, describedBy) => (
                <TextInput
                  id={id}
                  aria-describedby={describedBy}
                  required
                  placeholder="Platform"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                />
              )}
            </Field>

            <Button type="submit" variant="primary" disabled={createTeam.isPending}>
              {createTeam.isPending ? 'Creating…' : 'Create team'}
            </Button>

            {createTeam.isError && (
              <Banner tone="error">{errorMessage(createTeam.error)}</Banner>
            )}
          </form>

          <ul className="mt-5 flex flex-col gap-0.5">
            {teams.data?.map((team) => (
              <li key={team.id}>
                <button
                  type="button"
                  aria-current={team.id === selected ? 'page' : undefined}
                  onClick={() => setSelectedId(team.id)}
                  className={`flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5
                    text-left text-sm transition-colors
                    ${team.id === selected ? 'bg-accent-subtle text-text' : 'text-text-muted hover:text-text'}`}
                >
                  <span className="truncate">{team.name}</span>
                  <span className="shrink-0 text-xs text-text-faint">{team.yourRole}</span>
                </button>
              </li>
            ))}
          </ul>
        </aside>

        <main className="min-h-0 flex-1 overflow-y-auto p-6">
          <div className="mb-6 max-w-2xl">
            <InvitationsCard />
          </div>

          {teams.isPending ? (
            <Spinner label="Loading teams" />
          ) : selected ? (
            <TeamDetail teamId={selected} onDeleted={() => setSelectedId(null)} />
          ) : (
            <EmptyState title="No teams yet">
              A team gives everybody in it one shared library of snippets and tags.
            </EmptyState>
          )}
        </main>
      </div>
    </div>
  );
}

function TeamDetail({ teamId, onDeleted }: { teamId: string; onDeleted: () => void }) {
  const { data: me } = useSession();
  const team = useTeam(teamId);
  const invite = useInviteMember(teamId);
  const changeRole = useChangeRole(teamId);
  const removeMember = useRemoveMember(teamId);
  const deleteTeam = useDeleteTeam();

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<TeamRole>('Member');
  const [invited, setInvited] = useState<string | null>(null);

  if (team.isPending) {
    return <Spinner label="Loading team" />;
  }

  if (team.isError || !team.data) {
    return <Banner tone="error">{errorMessage(team.error, 'That team could not be loaded.')}</Banner>;
  }

  const detail = team.data;
  const canManageMembers = detail.yourRole === 'Admin' || detail.yourRole === 'Owner';
  const isOwner = detail.yourRole === 'Owner';

  return (
    <div className="flex max-w-2xl flex-col gap-6">
      <div>
        <h2 className="text-lg font-semibold text-text">{detail.name}</h2>
        <p className="text-sm text-text-muted">
          {detail.members.length} {detail.members.length === 1 ? 'member' : 'members'} · you are
          a{detail.yourRole === 'Admin' || detail.yourRole === 'Owner' ? 'n' : ''}{' '}
          {detail.yourRole.toLowerCase()}
        </p>
      </div>

      <TeamBillingPanel teamId={teamId} />

      {canManageMembers && (
        <form
          className="flex flex-col gap-2 rounded-md border border-border-subtle p-4"
          onSubmit={(event) => {
            event.preventDefault();
            invite.mutate(
              { email, role },
              {
                onSuccess: () => {
                  setInvited(email);
                  setEmail('');
                },
              },
            );
          }}
        >
          {invite.isError && <Banner tone="error">{errorMessage(invite.error)}</Banner>}

          {/*
            Deliberately says nothing about whether that address has an account here. The endpoint
            answers the same either way, and a message that gave it away would put the leak back in
            through the interface.
          */}
          {invited && !invite.isError && (
            <Banner tone="success">
              Invitation sent to {invited}. They join once they accept it.
            </Banner>
          )}

          <Field
            label="Invite by email"
            hint="They get an email and choose whether to join. Nothing changes for them until they accept."
          >
            {(id, describedBy) => (
              <TextInput
                id={id}
                aria-describedby={describedBy}
                type="email"
                required
                placeholder="colleague@example.com"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  setInvited(null);
                }}
              />
            )}
          </Field>

          <div className="flex items-end gap-2">
            <label className="flex flex-col gap-1 text-sm text-text">
              Role
              <select
                value={role}
                onChange={(event) => setRole(event.target.value as TeamRole)}
                className="rounded-md border border-border-subtle bg-surface px-2 py-1.5 text-sm text-text"
              >
                {roles
                  // Only an owner can mint another owner, so do not offer what the API refuses.
                  .filter((option) => option !== 'Owner' || isOwner)
                  .map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
              </select>
            </label>

            <Button type="submit" variant="primary" disabled={invite.isPending}>
              {invite.isPending ? 'Inviting…' : 'Invite'}
            </Button>
          </div>
        </form>
      )}

      <PendingInvitations teamId={teamId} canManage={canManageMembers} />

      <div>
        <h3 className="text-xs font-medium tracking-wide text-text-faint uppercase">Members</h3>

        {changeRole.isError && (
          <div className="mt-2">
            <Banner tone="error">{errorMessage(changeRole.error)}</Banner>
          </div>
        )}
        {removeMember.isError && (
          <div className="mt-2">
            <Banner tone="error">{errorMessage(removeMember.error)}</Banner>
          </div>
        )}

        <ul className="mt-2 flex flex-col gap-1">
          {detail.members.map((member) => {
            const isMe = member.userId === me?.id;

            return (
              <li
                key={member.userId}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md
                  bg-surface-raised px-3 py-2"
              >
                <span className="min-w-0">
                  <span className="block truncate text-sm text-text">
                    {member.displayName}
                    {isMe && <span className="ml-1 text-xs text-text-faint">(you)</span>}
                  </span>
                  <span className="block truncate text-xs text-text-muted">{member.email}</span>
                </span>

                <span className="flex items-center gap-2">
                  {canManageMembers ? (
                    <label className="sr-only" htmlFor={`role-${member.userId}`}>
                      Role for {member.displayName}
                    </label>
                  ) : null}

                  {canManageMembers ? (
                    <select
                      id={`role-${member.userId}`}
                      value={member.role}
                      onChange={(event) =>
                        changeRole.mutate({
                          userId: member.userId,
                          role: event.target.value as TeamRole,
                        })
                      }
                      className="rounded-md border border-border-subtle bg-surface px-2 py-1
                        text-xs text-text"
                    >
                      {roles
                        .filter((option) => option !== 'Owner' || isOwner)
                        .map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                    </select>
                  ) : (
                    <span className="text-xs text-text-muted">{member.role}</span>
                  )}

                  {(canManageMembers || isMe) && (
                    <Button
                      variant="ghost"
                      aria-label={isMe ? 'Leave this team' : `Remove ${member.displayName}`}
                      onClick={() => {
                        // One click away from a shared library disappearing, and there is no undo
                        // from this side: since invitations replaced adding people, getting back in
                        // means somebody still in the team sending a fresh invitation.
                        const question = isMe
                          ? `Leave ${detail.name}? You lose access to its library, and only somebody still in the team can invite you back.`
                          : `Remove ${member.displayName} from ${detail.name}? They lose access to its library, and would need a new invitation to return.`;

                        if (!window.confirm(question)) {
                          return;
                        }

                        removeMember.mutate(member.userId, {
                          onSuccess: () => {
                            if (isMe) {
                              onDeleted();
                            }
                          },
                        });
                      }}
                    >
                      {isMe ? 'Leave' : 'Remove'}
                    </Button>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      </div>

      {isOwner && (
        <div className="rounded-md border border-danger p-4">
          <h3 className="text-sm font-medium text-text">Delete this team</h3>
          <p className="mt-1 text-sm text-text-muted">
            Every snippet and tag in its library goes with it. This cannot be undone.
          </p>

          <Button
            variant="danger"
            className="mt-3"
            disabled={deleteTeam.isPending}
            onClick={() => {
              if (window.confirm(`Delete ${detail.name} and everything in its library?`)) {
                deleteTeam.mutate(teamId, { onSuccess: onDeleted });
              }
            }}
          >
            Delete team
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * Who has been asked, and what became of it.
 *
 * Declined and withdrawn invitations stay listed for a while rather than vanishing: an invitation
 * that simply disappears leaves an admin refreshing the page wondering whether the email ever
 * arrived, and inviting the same person again is the usual next move.
 */
function PendingInvitations({ teamId, canManage }: { teamId: string; canManage: boolean }) {
  const invitations = useTeamInvitations(teamId);
  const revoke = useRevokeTeamInvitation(teamId);
  const resend = useResendTeamInvitation(teamId);

  const all = invitations.data ?? [];
  if (all.length === 0) {
    return null;
  }

  return (
    <div>
      <h3 className="text-xs font-medium tracking-wide text-text-faint uppercase">Invitations</h3>

      {revoke.isError && (
        <div className="mt-2">
          <Banner tone="error">{errorMessage(revoke.error)}</Banner>
        </div>
      )}
      {resend.isError && (
        <div className="mt-2">
          <Banner tone="error">{errorMessage(resend.error)}</Banner>
        </div>
      )}

      <ul className="mt-2 flex flex-col gap-1">
        {all.map((invitation) => {
          const pending = invitation.status === 'Pending';
          const lapsed = pending && new Date(invitation.expiresAt) <= new Date();

          return (
            <li
              key={invitation.id}
              className="flex flex-wrap items-center justify-between gap-2 rounded-md
                bg-surface-raised px-3 py-2"
            >
              <span className="min-w-0">
                <span className="block truncate text-sm text-text">{invitation.email}</span>
                <span className="block truncate text-xs text-text-muted">
                  {invitation.role} ·{' '}
                  {!pending
                    ? invitation.status.toLowerCase()
                    : lapsed
                      ? 'expired — invite them again'
                      : `invited, expires ${new Date(invitation.expiresAt).toLocaleDateString()}`}
                </span>
              </span>

              {canManage && pending && (
                <span className="flex shrink-0 items-center gap-2">
                  <Button
                    variant="ghost"
                    disabled={resend.isPending}
                    onClick={() => resend.mutate(invitation.id)}
                  >
                    Resend
                  </Button>
                  <Button
                    variant="ghost"
                    aria-label={`Withdraw the invitation to ${invitation.email}`}
                    disabled={revoke.isPending}
                    onClick={() => revoke.mutate(invitation.id)}
                  >
                    Withdraw
                  </Button>
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
